import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Sparkles, BadgeCheck, MapPin } from "lucide-react";
import BusinessLogo from "@/components/BusinessLogo";

/**
 * "Discover Similar Black-Owned Businesses" horizontal rail rendered near
 * the bottom of every business profile. Backed by the Blinterest
 * category-match algorithm on /api/blinterest/business/{id}/related-businesses.
 * Silently hides when there are no relevant matches.
 */
export default function RelatedBusinessesRail({ businessId }) {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    axios
      .get(`${API}/blinterest/business/${businessId}/related-businesses?limit=8`)
      .then((r) => setBusinesses(r.data.businesses || []))
      .catch(() => setBusinesses([]))
      .finally(() => setLoading(false));
  }, [businessId]);

  if (!loading && businesses.length === 0) return null;

  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      data-testid="related-businesses-rail"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-tbc-gold" />
        <h2 className="font-heading text-tbc-navy text-lg sm:text-xl">
          Support Similar Black-Owned Businesses
        </h2>
      </div>
      <div className="overflow-hidden">
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x scrollbar-hidden">
          {loading
            ? [1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-56 flex-none h-32 bg-gray-100 rounded-xl animate-pulse"
                />
              ))
            : businesses.map((b) => (
                <Link
                  key={b.business_id}
                  to={`/business/${b.business_id}`}
                  className="w-56 flex-none snap-start rounded-xl overflow-hidden bg-white border border-gray-200 hover:border-tbc-gold/60 transition-colors p-3"
                  data-testid={`related-biz-${b.business_id}`}
                >
                  <div className="flex items-start gap-2">
                    <BusinessLogo
                      name={b.name}
                      logoUrl={b.logo_url}
                      size="md"
                      shape="rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-heading text-sm text-tbc-navy line-clamp-2 flex items-center gap-1">
                        <span className="truncate">{b.name}</span>
                        {b.is_verified && (
                          <BadgeCheck className="w-3.5 h-3.5 text-tbc-gold flex-none" />
                        )}
                      </p>
                      {b.category && (
                        <p className="text-[10px] text-gray-500 truncate">{b.category}</p>
                      )}
                      {b.city && b.state && (
                        <p className="text-[10px] text-gray-500 mt-0.5 inline-flex items-center gap-0.5">
                          <MapPin className="w-2.5 h-2.5" />
                          {b.city}, {b.state}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
}
